import {useContext, useEffect, useMemo, useRef} from 'react';

import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';

import {Box, CircularProgress, Grid, Typography, useTheme} from '@mui/material';

import {useBatchInvitations} from '../hooks/useBatchInvitations';
import {FilterOptions} from '../types/screeningExternalApplicantsTypes';

import {BatchInvitationsCsvUploadField} from './BatchInvitationsCsvUploadField';
import {BatchInvitationsFormFields} from './BatchInvitationsFormFields';
import {BatchInvitationsFormValues, Option} from './BatchInvitationsModal.types';
import {BatchInvitationsValidationErrors} from './BatchInvitationsValidationErrors';
import {EmpionButton} from '../../../../components/EmpionButton/EmpionButton';
import EmpionDialog from '../../../../components/EmpionDialog/EmpionDialog';
import {LanguageContext} from '../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../contexts/UserContext/UserContext';
import {CompanyAdminRoleData} from '../../../../types/User';

interface Props {
  open: boolean;
  onClose: () => void;
  filterOptions?: FilterOptions;
  refetch?: () => void;
  onConfirmSuccess?: (batchId: string) => void;
}

export const BatchInvitationsModal = ({open, onClose, filterOptions, refetch, onConfirmSuccess}: Props) => {
  const {t: tr} = useTranslation('translation', {keyPrefix: 'screeningModule.batchInvitationsModal'});
  const {t: ts} = useTranslation('translation', {keyPrefix: 'shared'});
  const theme = useTheme();
  const currentLanguage = useContext(LanguageContext);
  const {dispatchMessage} = useContext(SnackBarContext);
  const {currentUser} = useContext(UserContext);
  const companyId = (currentUser?.role.data as CompanyAdminRoleData)?.companyId || '';
  const createdByUserId = currentUser?.id;

  const jobPostingOptions = useMemo<Option[]>(
    () => (filterOptions?.jobPostings || []).map((option) => ({label: option, value: option})),
    [filterOptions?.jobPostings]
  );
  const locationOptions = useMemo<Option[]>(
    () => (filterOptions?.locations || []).map((option) => ({label: option, value: option})),
    [filterOptions?.locations]
  );
  const departmentOptions = useMemo<Option[]>(
    () =>
      (filterOptions?.departments || []).map((department) => ({
        label: department.name[currentLanguage],
        value: department.resourceId,
      })),
    [currentLanguage, filterOptions?.departments]
  );
  const jobPostingAutocompleteOptions = useMemo(
    () => jobPostingOptions.map((option) => option.value),
    [jobPostingOptions]
  );
  const locationAutocompleteOptions = useMemo(() => locationOptions.map((option) => option.value), [locationOptions]);
  const departmentLabel = String(tr('departmentLabel'));
  const departmentPlaceholder = String(tr('departmentPlaceholder') || '');
  const jobPostingLabel = String(tr('jobPostingLabel'));
  const jobPostingPlaceholder = String(tr('jobPostingPlaceholder') || '');
  const locationLabel = String(tr('locationLabel'));
  const locationPlaceholder = String(tr('locationPlaceholder') || '');
  const modalTitle = String(tr('title'));
  const wasOpenRef = useRef(open);
  const resetModalStateRef = useRef<() => void>(() => undefined);

  const {
    validateSuccess,
    batchResourceId,
    validationSummary,
    validationErrors,
    generalErrors,
    isValidating,
    isConfirming,
    setGeneralErrors,
    resetValidatedStateAndErrors,
    validateBatch,
    confirmBatch,
  } = useBatchInvitations({
    companyId,
    createdByUserId: createdByUserId ? createdByUserId : null,
    genericErrorMessage: String(tr('errors.generic')),
    confirmStateErrorMessage: String(tr('errors.confirmState')),
    malformedCsvErrorMessage: String(tr('errors.malformedCsv')),
    formatMalformedCsvLineErrorMessage: (line) => String(tr('errors.malformedCsvWithLine', {line})),
    rowGenericErrorMessage: String(tr('errors.rowGeneric')),
    onConfirmSuccess: (batchId) => {
      dispatchMessage(String(tr('success.batchStarted')), 'success');
      refetch?.();
      onConfirmSuccess?.(batchId);
      onClose();
    },
  });

  const formik = useFormik<BatchInvitationsFormValues>({
    initialValues: {
      departmentId: null,
      jobPosting: '',
      location: '',
      file: null,
    },
    validate: (values) => {
      const errors: Partial<Record<keyof BatchInvitationsFormValues, string>> = {};

      if (!values.jobPosting.trim()) {
        errors.jobPosting = String(ts('formValidation.required', {fieldName: tr('jobPostingLabel')}));
      }

      if (!values.location.trim()) {
        errors.location = String(ts('formValidation.required', {fieldName: tr('locationLabel')}));
      }

      if (!values.file) {
        errors.file = String(ts('formValidation.required', {fieldName: tr('csvLabel')}));
      }

      return errors;
    },
    onSubmit: () => undefined,
  });

  resetModalStateRef.current = () => {
    formik.resetForm();
    resetValidatedStateAndErrors();
  };

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      resetModalStateRef.current();
    }
    wasOpenRef.current = open;
  }, [open]);

  const updateFieldValue = <FieldName extends keyof BatchInvitationsFormValues>(
    fieldName: FieldName,
    value: BatchInvitationsFormValues[FieldName]
  ) => {
    formik.setFieldValue(fieldName, value);
    resetValidatedStateAndErrors();
  };

  const handleDepartmentChange = (value: Option | null) => {
    updateFieldValue('departmentId', value?.value || null);
  };

  const handleJobPostingChange = (value: string) => {
    updateFieldValue('jobPosting', value);
  };

  const handleLocationChange = (value: string) => {
    updateFieldValue('location', value);
  };

  const handleFileChange = (selectedFile: File | null) => {
    if (!selectedFile) {
      updateFieldValue('file', null);
      return;
    }

    const isCsv = selectedFile.name.toLowerCase().endsWith('.csv') || selectedFile.type.includes('csv');
    if (!isCsv) {
      updateFieldValue('file', null);
      setGeneralErrors([tr('errors.invalidFileType')]);
      return;
    }

    updateFieldValue('file', selectedFile);
  };

  const handleValidate = async () => {
    formik.setTouched({departmentId: true, jobPosting: true, location: true, file: true}, true);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }

    validateBatch(formik.values);
  };

  const handleConfirm = () => {
    confirmBatch();
  };

  const isValidateDisabled =
    isValidating || !formik.values.jobPosting.trim() || !formik.values.location.trim() || !formik.values.file;
  const isConfirmDisabled = isValidating || isConfirming || !validateSuccess || !batchResourceId;

  return (
    <EmpionDialog
      open={open}
      onClose={onClose}
      title={modalTitle}
      showCloseButton
      backgroundColor='#fff'
      sxTitle={{
        fontSize: '28px',
        fontWeight: 700,
        lineHeight: '42px',
      }}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '736px',
            padding: '24px',
            borderRadius: '16px',
          },
        },
      }}
    >
      <Box marginTop={2}>
        <Grid container spacing={2}>
          <BatchInvitationsFormFields
            formik={formik}
            theme={theme}
            departmentOptions={departmentOptions}
            jobPostingOptions={jobPostingAutocompleteOptions}
            locationOptions={locationAutocompleteOptions}
            departmentLabel={departmentLabel}
            departmentPlaceholder={departmentPlaceholder}
            jobPostingLabel={jobPostingLabel}
            jobPostingPlaceholder={jobPostingPlaceholder}
            locationLabel={locationLabel}
            locationPlaceholder={locationPlaceholder}
            onDepartmentChange={handleDepartmentChange}
            onJobPostingChange={handleJobPostingChange}
            onLocationChange={handleLocationChange}
          />
          <Grid item xs={12}>
            <BatchInvitationsCsvUploadField
              file={formik.values.file}
              error={formik.touched.file ? formik.errors.file : undefined}
              label={tr('csvLabel')}
              dropzoneCta={tr('csvDropzoneCta')}
              acceptedFormatLabel={tr('csvAcceptedFormat')}
              selectedFileLabel={tr('selectedFile', {fileName: formik.values.file?.name || ''})}
              onFileSelect={handleFileChange}
            />
          </Grid>
          <BatchInvitationsValidationErrors generalErrors={generalErrors} validationErrors={validationErrors} />
          {validateSuccess && (
            <Grid item xs={12}>
              <Typography variant='body2' color='success.main'>
                {tr('validationSummary', {
                  validRowsCount: validationSummary.validRowsCount ?? 0,
                  totalRowsCount: validationSummary.totalRowsCount ?? 0,
                })}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
            <EmpionButton type='button' variant='secondary' onClick={handleValidate} disabled={isValidateDisabled}>
              {isValidating ? <CircularProgress size={20} color='inherit' /> : tr('validateButton')}
            </EmpionButton>
            <EmpionButton type='button' variant='primary' onClick={handleConfirm} disabled={isConfirmDisabled}>
              {isConfirming ? <CircularProgress size={20} color='inherit' /> : ts('formButtons.confirm')}
            </EmpionButton>
          </Grid>
        </Grid>
      </Box>
    </EmpionDialog>
  );
};
